import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down.svg";
import { ReactComponent as IconPerson } from "@/assets/svgs/icon-person-request.svg";
import { ReactComponent as IconNoUser } from "@/assets/svgs/no-user-added.svg";
import Button from "@/components/Button/index";
import DatePicker from "@/components/DatePicker/index";
import LoadingPortal from "@/components/LoadingPortal/index";
import MultiSelectCheckbox from "@/components/MultiSelectCheckbox/index";
import StatusLabel from "@/components/StatusLabel/index";
import Tabs, { Tab } from '@/components/Tabs';
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index";
import Search from '@/componentsDS/Search/index';
import { featureConstant } from "@/constants/configuration/feature";
import useApprovalRequest from "@/hooks/use-approval-request/index";
import useDebounce from "@/hooks/use-debounce/index";
import usePage from "@/hooks/use-page";
import useUseCases from '@/hooks/use-use-cases';
import { useNavigate, useQuery } from "@/lib/router-dom/index";
import { feature } from "@/utils/feature-verifier";
import { getDexConfig } from "@/utils/get-dex-config";
import { getEnvAndDex } from "@/utils/get-env-and-dex";
import titleCase from '@/utils/title-case';
import { uniqueId } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useFeature } from "@/hooks/use-feature/index.js";
import { renderUserEmailStacked, getTooltipAttributes } from '@/utils/formatUserEmail';
import { useTradex } from "@/hooks/use-tradex/index";

const Page = () => {
	/*=================================================1
		General
	/================================================*/
	const isSoftDeleteUserEnabled = useFeature(featureConstant.SOFT_DELETE_USER);
	const [pageTitle, setPageTitle] = useState("Approval Requests");
	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: pageTitle,
				link: ``
			},
		]
	 });
	 const { dex } = getEnvAndDex();
	const dexConfig = getDexConfig(dex);
	const isTradex = useTradex();

	const searchParams = useQuery();
	const search = searchParams.get('search')

	const { goTo } = useNavigate();

	const requestType = {
		SGTD: "SGTRADEX_ENROLLMENT",
		ENABLE_AGENT: "ENABLE_AGENT_ENROLMENT",
		DISABLE_AGENT: "DISABLE_AGENT_ENROLMENT",
		USECASE: "USE_CASE_ENROLLMENT",
		PERSONA_ADD: "PERSONA_CHANGE_ADD",
		PERSONA_REMOVE: "PERSONA_CHANGE_REMOVE"
	}

	//================================================
	// Search / filter
	//================================================
	const [searchParticipant, setSearchParticipant] = useState(search || "")
	const debouncedFetchSortedData = useDebounce(fetchSortedData, "dataFiltering");

	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);

	useEffect(() => {
		return setEndDate(null);
	}, [startDate]);

	const requestItems = [
		"SGTraDex Enrollment",
		"Enable Agent Enrolment",
		"Disable Agent Enrolment",
		"Use Case Enrollment",
		"Persona Change - Add",
		"Persona Change - Remove"
	]
	const requestFilter = requestItems.map((item, index) => ({ id: `${index}`, label: item }))
	const [filterRequestType, setFilterRequestType] = useState(requestFilter)

	const handleChange =(e) =>{
		setSearchParticipant(e.target.value)
	}
	const handleCancel =()=> setSearchParticipant('')

	//================================================
	// Pagination
	//================================================
	const [currentPage, setCurrentPage] = useState(1);
	const pageLimit = 10;

	//================================================
	// Sort
	//================================================
	const [sortConfig, setSortConfig] = useState({
		sortBy: "created",
		direction: false,
	});

	//================================================
	// Text Conversion
	//================================================
	function convertToDisplay(text) {
		if (text === "SGTRADEX_ENROLLMENT") return `${dexConfig.dexName} Enrollment`
		if (text === "ENABLE_AGENT_ENROLMENT") return "Enable Agent - Principal Function"
		if (text === "DISABLE_AGENT_ENROLMENT") return "Disable Agent - Principal Function"
		if (text === "PERSONA_CHANGE_ADD") return "Persona Change - Add"
		if (text === "PERSONA_CHANGE_REMOVE") return "Persona Change - Remove"
		if (text === "DSA_REQUESTED") return "Sign DSA"
		if (text === "SIGN_DSA") return "Sign DSA"
		if (text === "INFO_REQUESTED") return "Request for Info"
		if (text === "PENDING") return "Pending Approval"

		const str = text.split("_")
		return str?.map((str) => titleCase(str)).join(" ")
	}

	function convertText(text) {
		if (text === "SGTraDex Enrollment") return "SGTRADEX_ENROLLMENT"
		if (text === "Enable Agent - Principal Function") return "ENABLE_AGENT_ENROLMENT"
		if (text === "Disable Agent - Principal Function") return "DISABLE_AGENT_ENROLMENT"
		if (text === "Persona Change - Add") return "PERSONA_CHANGE_ADD"
		if (text === "Persona Change - Remove") return "PERSONA_CHANGE_REMOVE"

		const str = text.split(" ")
		return str.map((str) => str.toUpperCase()).join("_")
	}

	function convertArrayToString(array) {

		if (!Array.isArray(array)) {
			return null
		}

		if (array.every(item => item.$selected === true)) {
			return null
		}

		const tempArray = retrieveId(array)
		if (tempArray.length > 0) {
			return tempArray.toString();
		}

		return null
	}

	function retrieveId(array) {
		const tempArray = [];

		array.forEach(item => {
			if (item.$selected) {
				tempArray.push(item.id)
			}
		})

		return tempArray
	}

	function convertRequestToString(array) {

		if (!Array.isArray(array)) {
			return null
		}

		if (array.every(item => item.$selected === true)) {
			return null
		}

		const tempArray = [];

		array.forEach(item => {
			if (item.$selected) {
				tempArray.push(convertText(item.label))
			}
		})

		return tempArray.toString()
	}

	/*=================================================
		Tabs
	/================================================*/

	const [tabsItems, setTabsItems] = useState([
		{ label: `For Review ()`, active: true },
		{ label: 'Processed', active: false }
	])

	function selectTab(index) {
		const items = [...tabsItems].map(item => ({ ...item, active: false }))
		items[index].active = true
		setTabsItems(items)
	}

	/*=================================================
	  API
	/================================================*/

	const { getFilterUseCase, filterUseCase, setFilterUseCase } = useUseCases()
	const { fetchApprovalRequests: fetchRequestList, itemsApprovalRequests: requestList, metaApprovalRequests: requestMeta, loadingApprovalRequests, getRequestCountApprovalRequests, reviewCountApprovalRequests } = useApprovalRequest();

	function fetchSortedData() {
		if (!loadingApprovalRequests)
			fetchRequestList({
				useCaseId: convertArrayToString(filterUseCase) ?? null,
				requestType: convertRequestToString(filterRequestType) ?? null,
				dateFrom: startDate !== null ? moment(startDate).format("yyyy-MM-DD HH:mm") : null,
				dateTo: endDate !== null ? moment(endDate).format("yyyy-MM-DD HH:mm") : null,
				page: currentPage,
				limit: pageLimit,
				sortBy: sortConfig.sortBy,
				sortOrder: sortConfig.direction === true ? "ASC" : "DESC",
				searchParticipant: searchParticipant,
				statusType: tabsItems[0].active ? "PENDING_REQUESTS" : "PROCESSED_REQUESTS",
			});
	}

	useEffect(() => {
		getFilterUseCase()
		getRequestCountApprovalRequests({ statusType: "PENDING_REQUESTS" })
	}, [])

	useEffect(() => {
		fetchSortedData();
	}, [currentPage, sortConfig, startDate, endDate, filterUseCase, filterRequestType, tabsItems])

	useEffect(() => {
		debouncedFetchSortedData();
	}, [searchParticipant]);

	useEffect(() => {
		setTabsItems([
			{ label: `For Review (${reviewCountApprovalRequests})`, active: true },
			{ label: 'Processed', active: false }
		])
	}, [reviewCountApprovalRequests])

	/*=================================================
		Sort
	/================================================*/

	function handleSort(sortBy, type) {
		let direction = sortConfig.direction;
		if (sortConfig.sortBy === sortBy) {
			direction = !direction;
		} else {
			direction = true;
		}

		setSortConfig({ sortBy, direction });
	}

	function clearFilter() {
		getFilterUseCase()
		setStartDate(null)
		setEndDate(null)
		setSearchParticipant("")
		setFilterRequestType(requestFilter)
	}

	/*=================================================
	  Pagination
	/================================================*/

	function goToPrev() {
		if (currentPage === 1) {
			return;
		}

		setCurrentPage((prev) => prev - 1);
	}

	function goToNext() {
		if (currentPage === requestMeta?.totalPages) {
			return;
		}

		setCurrentPage((prev) => prev + 1);
	}


	/*=================================================
		Data Table
	/================================================*/

	function getClassName(name) {
		if (!sortConfig) {
			return;
		}
		return sortConfig.sortBy === name
			? sortConfig.direction === true ? "is-asc" : "is-desc"
			: "";
	}

	function getPageStart(pageSize, pageNr) {
		return pageSize * pageNr;
	}

	function getPageLabel(total, pageSize, pageNr) {
		const start = Math.max(getPageStart(pageSize, pageNr), 0);
		const end = Math.min(getPageStart(pageSize, pageNr + 1), total);

		return `${start + 1} - ${end}`;
	}

	function handleCurrentInput(e) {
		const value = e.target?.value === "" ? 1 : e.target.value;
		const current = parseInt(value);

		if (current < 1) {
			return setCurrentPage(1);
		}
		setCurrentPage(current);
	}

	const NoMessage = () => {
		return (
			<div className="data-table__empty__icon">
				<IconNoUser />
				<span>No Request</span>
			</div>
		);
	};

	const list = requestList?.map((item) => {
		const id = uniqueId();

		return (
			<div className="data-table__table__body__row" data-testid={item.id} key={id}>
				<div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--heavy  data-table__flex request-table__flex_item">
					{item.requestType === requestType.SGTD && (
						<div className="approval-request__avatar-admin"><IconPerson /></div>
					)}
					{(item.requestType !== requestType.SGTD) && (
						<div className="approval-request__avatar-org" style={{ backgroundImage: `url(${item.user?.organization?.iconUrl})` }}></div>
					)}
					{item?.organization?.name}
				</div>
				<div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--break request-table__flex_item--name account-settings__modal_term-list__term-date" {...getTooltipAttributes(isSoftDeleteUserEnabled, item.user)}>
					{isSoftDeleteUserEnabled ? renderUserEmailStacked(item?.user) : item?.user?.email}
				</div>
				<div className="data-table__table__body__content data-table__table__body__content--text request-table__flex_item--type">
					{convertToDisplay(item.requestType)}
				</div>
				<div className="data-table__table__body__content data-table__table__body__content--text request-table__flex_item--date">
					{moment(item.created).format("M/D/YYYY")}
				</div>

				<div className="data-table__table__body__content data-table__table__body__content--text">
					<StatusLabel text={convertToDisplay(item.status)} itemStatus={item.status} />
				</div>
				<div className="data-table__table__body__content data-table__table__body__content__button">
					<Button text="View" ghost submit={() => goToView(item)} />
				</div>
			</div>
		);
	});

	const DataTable = (
		<div className="data-table__content">
			<div className="data-table__content__cols">
				<div className="data-table__content__col">
					<div className="data-table__table">
						<div className="data-table__table__head">
							<div className="data-table__table__head__row">
								<div
									className={`data-table__table__head__title  data-table__table__head__title__sort-order request-table__flex_item ${getClassName("participantName")}`}
									id="participantName"
									onClick={(e) => { handleSort("participantName"); }}
								>
									Organization of Requestor
									<span className="data-table__table__head__icon__sort-order"></span>
								</div>
								<div
									className={`data-table__table__head__title  data-table__table__head__title__sort-order request-table__flex_item--name ${getClassName("email")}`}
									id="email"
									onClick={(e) => { handleSort("email"); }}
								>
									Name of Requestor
									<span className="data-table__table__head__icon__sort-order"></span>
								</div>

								<div
									className={`data-table__table__head__title  data-table__table__head__title__sort-order request-table__flex_item--type ${getClassName("requestType")}`}
									id="requestType"
									onClick={(e) => { handleSort("requestType"); }}
								>
									Request Type
									<span className="data-table__table__head__icon__sort-order"></span>
								</div>

								<div
									className={`data-table__table__head__title  data-table__table__head__title__sort-order request-table__flex_item--date ${getClassName("created")}`}
									id="created"
									onClick={(e) => {
										handleSort("created");
									}}
								>
									Date of Request
									<span className="data-table__table__head__icon__sort-order"></span>
								</div>

								<div
									className={`data-table__table__head__title  data-table__table__head__title__sort-order ${getClassName("status")}`}
									id="status"
									onClick={(e) => {
										handleSort("status");
									}}
								>
									Status
									<span className="data-table__table__head__icon__sort-order"></span>
								</div>

								<div className="data-table__table__head__title"></div>
							</div>
						</div>

						{list.length > 0 ? (
							<div className="data-table__table__body">
								{list}
							</div>
						) : (
							<div className="data-table__empty">
								<NoMessage />
							</div>
						)}

						{list.length > 0 && (
							<div className="data-table__table__footer">
								<div className="data-table__table__footer__row">
									<div className="data-table__table__footer__row__left">
										<div className="data-table__table__pagination">
											<input className="data-table__table__pagination__page" type="number" value={currentPage} onChange={(e) => handleCurrentInput(e)} min="1" />
											<span className="data-table__table__pagination__page__meta"> of {requestMeta?.totalPages || 1} pages </span>
											<button className="data-table__table__pagination__prev" onClick={() => goToPrev()} disabled={currentPage === 1}><IconChevronDown /></button>
											<button className="data-table__table__pagination__next" onClick={() => goToNext()} disabled={currentPage === requestMeta?.totalPages}><IconChevronDown /></button>
										</div>
									</div>
									<div className="data-table__table__footer__row__right">
										<div className="data-table__table__meta">
											Showing {getPageLabel(requestMeta?.total, pageLimit, currentPage - 1)} of {requestMeta?.total || 0} entries
										</div>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);


	/*=================================================
		Navigate
	/================================================*/

	function goToView(item) {
		if (item.requestType === requestType.ENABLE_AGENT) {
			return goTo(`/admin/approval-requests/agent/${item.id}/${item?.organizationUseCases[0]?.useCaseId}`, { requestTypeStatus: "Enable" });
		}
		if (item.requestType === requestType.DISABLE_AGENT) {
			return goTo(`/admin/approval-requests/agent/${item.id}/${item?.organizationUseCases[0]?.useCaseId}`, { requestTypeStatus: "Disable" });
		}
		if (item.requestType === requestType.SGTD) {
			return goTo(`/admin/approval-requests/sgtd-enrollment/${item.id}/${item.orgPersona[0]?.useCase?.useCaseId}/${item.orgId}`);
		}
		if (item.requestType === requestType.USECASE) {
			return goTo(`/admin/approval-requests/use-case-enrollment/${item.id}/${item.orgPersona[0]?.useCase?.useCaseId}`);
		}
		if (item.requestType === requestType.PERSONA_ADD) {
			return goTo(`/admin/approval-requests/add-persona/${item.id}`);
		}
		if (item.requestType === requestType.PERSONA_REMOVE) {
			return goTo(`/admin/approval-requests/remove-persona/${item.id}`);
		}
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitle}
					</div>
				</div>
			</div>

			<Tabs>
				{tabsItems.map((item, index) => (
					<Tab key={index} label={item.label} active={item.active} onClick={() => selectTab(index)} />
				))}
			</Tabs>
			<br />

			<div className="overview">
				<div className="overview_filter">
					<div className="overview_filter__cols">
						<div className="overview_filter__col">
							<div className="overview_filter__col__top overview_filter__col__title">
								Participant Name
							</div>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--background">
{
	feature(featureConstant.NEW_DESIGN_SYSTEM) ?(
		<Search value={searchParticipant || ''} placeholder="Enter participant name" onChange={handleChange} onCancel={handleCancel} />

	): (

		<input type="text"
			value={searchParticipant || ''}
			onChange={(e) => setSearchParticipant(e.target.value)}
			placeholder="Enter participant name"
			className="data-table__table__search filter-participant__container"
		/>
	)
}
							</div>
						</div>

						<div className="overview_filter__col">
							<div className="overview_filter__col__top overview_filter__col__title">
								Start date
							</div>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--background">
								<DatePicker
									selectDate={startDate}
									setSelectDate={setStartDate}
									placeHolderText="Pick date and time"
								/>
							</div>
						</div>
						<div className="overview_filter__col">
							<div className="overview_filter__col__top">
								&nbsp;
							</div>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--padding-top-bottom ">
								–
							</div>
						</div>
						<div className="overview_filter__col">
							<div className="overview_filter__col__top">
								End date
							</div>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--background">
								<DatePicker
									selectDate={endDate}
									setSelectDate={setEndDate}
									placeHolderText="Pick date and time"
									disable={!startDate}
									minDate={new Date(startDate)}
									minTime={
										(startDate && endDate) ?
											((startDate.getDate() === endDate.getDate()) ?
												new Date(startDate)
												: new Date().setHours(0, 0, 0)) :
											new Date(0, 0, 0)
									}
									maxTime={new Date().setHours(23, 59, 59)}
								/>
							</div>
						</div>

						{isTradex && <div className="overview_filter__col">
							<div className="overview_filter__col__top overview_filter__col__title ">
								Request Type
							</div>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--background filter-request-type__container">
								<MultiSelectCheckbox
									items={filterRequestType}
									onChangeFunc={setFilterRequestType}
								/>
							</div>
						</div>}

						{isTradex && <div className="overview_filter__col">
							<div className="overview_filter__col__top overview_filter__col__title">
								Use Case
							</div>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--background">
								<MultiSelectCheckbox
									items={filterUseCase}
									onChangeFunc={setFilterUseCase}
								/>
							</div>
						</div>}

						<div className="overview_filter__col ">
							<div className="overview_filter__col__top">
								&nbsp;
							</div>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--background">
								<Button addClass="button--ghost button--no-border" text="Clear filters" noborder submit={() => clearFilter()} />
							</div>
						</div>
					</div>
				</div>
				<div className="overview__content">
					<div className="">{DataTable}</div>
				</div>
			</div>

				<LoadingPortal isLoading={loadingApprovalRequests}/>
		</>
	);
};

export default Page;
